/** Fechas de cada jornada en torneos sin calendario de canchas (Eduardo
 * 2026-10-04): «Jornada 3 · del lunes 6 al domingo 12 de octubre». Desde el
 * 2026-10-05 ya no se muestra «juega antes del domingo» (sobra: el rango ya
 * termina ese día). Fechas ISO (AAAA-MM-DD) leídas como día de calendario,
 * sin zona horaria. */

const WEEKDAYS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MONTHS = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

interface DayParts {
  year: number;
  month: number;
  day: number;
  weekday: string;
}

function parts(iso: string): DayParts | null {
  const found = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!found) return null;
  const year = Number(found[1]);
  const month = Number(found[2]);
  const day = Number(found[3]);
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(utc.getTime()) || utc.getUTCDate() !== day) return null;
  return { year, month, day, weekday: WEEKDAYS[utc.getUTCDay()] };
}

/** «del lunes 6 al domingo 12 de octubre», «del lunes 29 de septiembre al
 * domingo 5 de octubre», «el lunes 6 de octubre». */
export function jornadaRangeText(startsOn: string, endsOn: string): string | null {
  const start = parts(startsOn);
  const end = parts(endsOn);
  if (!start || !end) return null;
  const startMonth = MONTHS[start.month - 1];
  const endMonth = MONTHS[end.month - 1];
  if (startsOn.slice(0, 10) === endsOn.slice(0, 10)) {
    return `el ${start.weekday} ${start.day} de ${startMonth}`;
  }
  if (start.year !== end.year) {
    return `del ${start.weekday} ${start.day} de ${startMonth} de ${start.year} al ${end.weekday} ${end.day} de ${endMonth} de ${end.year}`;
  }
  if (start.month !== end.month) {
    return `del ${start.weekday} ${start.day} de ${startMonth} al ${end.weekday} ${end.day} de ${endMonth}`;
  }
  return `del ${start.weekday} ${start.day} al ${end.weekday} ${end.day} de ${endMonth}`;
}

const MONTHS_SHORT = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/** Rango corto para celdas angostas (póster del Draw, Eduardo 2026-10-20):
 * «6 – 12 oct», «25 nov – 2 dic», «28 dic 2026 – 3 ene 2027», «6 oct».
 * Sin día de la semana; año solo si cruza de año. Null sin fechas válidas. */
export function jornadaRangeShort(startsOn: string | null | undefined, endsOn: string | null | undefined): string | null {
  const start = parts(startsOn ?? "");
  const end = parts(endsOn ?? "");
  if (!start || !end) return null;
  const sm = MONTHS_SHORT[start.month - 1];
  const em = MONTHS_SHORT[end.month - 1];
  if (start.year === end.year && start.month === end.month && start.day === end.day) return `${start.day} ${sm}`;
  if (start.year !== end.year) return `${start.day} ${sm} ${start.year} – ${end.day} ${em} ${end.year}`;
  if (start.month !== end.month) return `${start.day} ${sm} – ${end.day} ${em}`;
  return `${start.day} – ${end.day} ${em}`;
}

/** Para los PDF (Eduardo 2026-10-05: «Jornada 1 de xx a xx del 2026»): el
 * rango con su año, «del martes 6 al domingo 11 de octubre de 2026»; si cruza
 * de año, cada fecha ya lleva el suyo («del lunes 28 de diciembre de 2026 al
 * domingo 3 de enero de 2027»). Null sin fechas válidas. */
export function jornadaRangeWithYear(startsOn: string, endsOn: string): string | null {
  const range = jornadaRangeText(startsOn, endsOn);
  const start = parts(startsOn);
  const end = parts(endsOn);
  if (!range || !start || !end) return null;
  return start.year !== end.year ? range : `${range} de ${end.year}`;
}

/** «Jornada 1 · del martes 6 al domingo 11 de octubre de 2026»; sin rango,
 * el nombre tal cual. */
export function jornadaTitleWithRange(label: string, range: string | null | undefined): string {
  return range ? `${label} · ${range}` : label;
}

type SavedWindowLike = { division_id: number | null; round_number: number; starts_on: string; ends_on: string };

type SavedRange = { divisionId: number | null; range: string };

function savedRangesWith(
  windows: ReadonlyArray<SavedWindowLike>,
  roundNumber: number,
  divisionIds: Iterable<number | null>,
  format: (startsOn: string, endsOn: string) => string | null,
): SavedRange[] | null {
  const out: SavedRange[] = [];
  for (const divisionId of new Set(divisionIds)) {
    const window = windows.find(
      (item) => item.round_number === roundNumber && (item.division_id ?? null) === (divisionId ?? null),
    );
    const range = window ? format(window.starts_on.slice(0, 10), window.ends_on.slice(0, 10)) : null;
    if (!range) return null;
    out.push({ divisionId: divisionId ?? null, range });
  }
  return out.length > 0 ? out : null;
}

/** El único rango si todos coinciden; null si varían o no hay. */
function sameRange(ranges: SavedRange[] | null): string | null {
  if (!ranges || new Set(ranges.map((item) => item.range)).size !== 1) return null;
  return ranges[0].range;
}

/** Rango GUARDADO por el organizador de la jornada `roundNumber` en cada una
 * de las categorías `divisionIds` (las de los partidos de la sección del
 * PDF), en ese orden y sin repetir. Solo si TODAS tienen fechas guardadas
 * (nunca las sugeridas); null si alguna no tiene. */
export function savedJornadaRangesByDivision(
  windows: ReadonlyArray<SavedWindowLike>,
  roundNumber: number,
  divisionIds: Iterable<number | null>,
): SavedRange[] | null {
  return savedRangesWith(windows, roundNumber, divisionIds, jornadaRangeWithYear);
}

/** El rango de la jornada si TODAS las categorías de la sección tienen las
 * mismas fechas guardadas. Si varían, null: un rango «del primer inicio al
 * último fin» no sería el de ninguna (Eduardo 2026-10-05; el PDF las pone
 * por categoría con `savedJornadaRangesByDivision`). */
export function savedJornadaRange(
  windows: ReadonlyArray<SavedWindowLike>,
  roundNumber: number,
  divisionIds: Iterable<number | null>,
): string | null {
  return sameRange(savedJornadaRangesByDivision(windows, roundNumber, divisionIds));
}

/** Como `savedJornadaRange`, pero corto («25 nov – 2 dic») para el póster. */
export function savedJornadaRangeShort(
  windows: ReadonlyArray<SavedWindowLike>,
  roundNumber: number,
  divisionIds: Iterable<number | null>,
): string | null {
  return sameRange(savedRangesWith(windows, roundNumber, divisionIds, jornadaRangeShort));
}

/** Eduardo 2026-10-05 («también en admin agrega fechas»): el rango GUARDADO
 * de la jornada debajo del selector de Resultados y Calendario del
 * organizador, sin año como en «Juegos» del jugador («del martes 6 al
 * domingo 11 de octubre»). Quien llama pasa la categoría elegida o, sin
 * categoría, las de la jornada en pantalla: solo si todas tienen las mismas
 * fechas guardadas (nunca las sugeridas); si no, null. */
export function savedJornadaScreenRange(
  windows: ReadonlyArray<SavedWindowLike>,
  roundNumber: number,
  divisionIds: Iterable<number | null>,
): string | null {
  return sameRange(savedRangesWith(windows, roundNumber, divisionIds, jornadaRangeText));
}

/** Las fechas y el plazo por separado («del lunes 6 al domingo 12 de
 * octubre» y «juega antes del domingo»). El plazo ya no se muestra en
 * ninguna pantalla (Eduardo 2026-10-05: sobra, el rango ya termina ese día);
 * el inicio del torneo solo usa `range`. Null sin fechas válidas. */
export function jornadaWindowParts(
  startsOn: string | null | undefined,
  endsOn: string | null | undefined,
): { range: string; deadline: string } | null {
  if (!startsOn || !endsOn) return null;
  const range = jornadaRangeText(startsOn, endsOn);
  const end = parts(endsOn);
  if (!range || !end) return null;
  const deadline = startsOn.slice(0, 10) === endsOn.slice(0, 10) ? "juega ese día" : `juega antes del ${end.weekday}`;
  return { range, deadline };
}

/** Renglón completo para el jugador; null sin fechas válidas. Sin «juega
 * antes del…» (Eduardo 2026-10-05: sobra, el rango ya termina ese día). */
export function jornadaWindowLine(
  label: string | null | undefined,
  startsOn: string | null | undefined,
  endsOn: string | null | undefined,
): string | null {
  const window = jornadaWindowParts(startsOn, endsOn);
  if (!window) return null;
  const name = (label ?? "").trim();
  return [name || null, window.range].filter(Boolean).join(" · ");
}

function roundLabelKey(label: string | null | undefined): string {
  return (label ?? "").trim().replace(/\s+/g, " ");
}

/** Ventana de la jornada de un partido en «Juegos». El servidor manda una
 * por par categoría/round_label de los partidos visibles y ya resolvió cuál
 * fila aplica («Playoff 1», «Semana 3», la escalera de torneo solo para las
 * categorías que heredan su formato): aquí solo se busca ese par. */
export function windowForMatch<W extends { division_id: number | null; round_label: string }>(
  windows: W[],
  match: { division_id?: number | null; round_label?: string | null },
): W | null {
  const label = roundLabelKey(match.round_label);
  if (!label) return null;
  const division = match.division_id ?? null;
  return windows.find((w) => w.division_id === division && roundLabelKey(w.round_label) === label) ?? null;
}

function toUtc(iso: string): number | null {
  const p = parts(iso);
  return p ? Date.UTC(p.year, p.month - 1, p.day) : null;
}

/** Días de `from` a `to` (positivo si `to` es después). */
export function daysBetween(from: string, to: string): number | null {
  const a = toUtc(from);
  const b = toUtc(to);
  if (a === null || b === null) return null;
  return Math.round((b - a) / 86_400_000);
}
